'use client'

import * as React from 'react'
import * as NavigationMenuPrimitive from 'primitives/navigation-menu'
import { useTranslations } from 'next-intl'
import { StudioIconLogo } from 'primitives/icons/local'
import { Button } from '@repo/studio-design-system-ui-button'
import { tw } from 'utils/classnames'
import { File04Icon } from 'primitives/icons/files'
import { ArrowNarrowRightIcon02, SearchMd02Icon } from 'primitives/icons/general'
import { TemplatesSearchWithProvider } from '@repo/studio-components-templates/algolia/search-input'
import { SearchInput as SearchInputUncontrolled } from 'components/search-input'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { Link } from '@repo/web-ui/components/link'
import { LanguageSwitcher } from 'cf-menu/components/desktop/language-and-currency'
import { useHeaderContext } from 'cf-menu/contexts/header-config-context'

import { NavigationMenu } from '@web/components/navigation/navigation-menu'
import { HamburgerMenu } from '@web/components/navigation/hamburger-menu'
import { useMenuItems } from '@web/hooks/use-menu-items'
import type { StudioHeaderComponentProps } from './'
import { SearchMobile } from '../search-mobile'
import { StyledLink } from '../styled-link'
export function StudioHeader({ iconColor }: StudioHeaderComponentProps) {
  const t = useTranslations('components.studio_header')
  const menuItems = useMenuItems()
  const { lang } = useHeaderContext()

  const searchInputProps = {
    placeholder: t('search.placeholder'),
    containerClassName: 'p-3 border-[var(--navigation-search-border-color)] mx-auto bg-transparent',
    className:
      'bg-[var(--navigation-background-color)] text-[var(--navigation-search-text-input-color)] placeholder-[var(--navigation-search-text-input-color)]',
    InputIcon: (
      <SearchMd02Icon
        className="h-6 w-6 text-[var(--navigation-search-icon-color)]"
        style={{ transform: 'translateZ(0)' }}
      />
    ),
    resultsClassName:
      'bg-[var(--navigation-background-color)] text-[var(--navigation-search-text-input-color)] border-[var(--navigation-search-border-color)]',
  }
  return (
    <div className="z-[1] flex h-[111px] flex-col items-center justify-between">
      <div className="flex h-[64px] w-full items-center justify-between border-b border-[var(--navigation-border-color)] pl-3 pr-4 sm:pl-6">
        <div className="flex items-center gap-2">
          <div className="xl:hidden">
            <HamburgerMenu />
          </div>
          <Link
            href={AppRoutes.STUDIO}
            className="outline-none"
            style={{ transform: 'translateZ(0)' }}
          >
            <StudioIconLogo
              style={{ transform: 'translateZ(0)' }}
              aria-label="Studio tool logo"
              className={tw(
                'relative top-[-4px] w-[69px] sm:top-[-2px] sm:h-[33px] sm:w-[93px]',
                iconColor,
              )}
            />
          </Link>
        </div>
        <div className="mx-6 hidden w-full max-w-[930px] items-center justify-center sm:flex">
          <React.Suspense fallback={<SearchInputUncontrolled {...searchInputProps} />}>
            <TemplatesSearchWithProvider
              searchComponentProps={searchInputProps}
              resultClassName="hover:bg-[var(--navigation-search-result-hover-background-color)]"
              NoResults={
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-800">{t('search.no_results')}</span>
                  <a
                    href={AppRoutes.STUDIO_TEMPLATES}
                    className={`flex items-center text-nowrap font-medium text-violet-700`}
                  >
                    {t('search.all_templates')}
                    <ArrowNarrowRightIcon02 className="ml-1 h-4 w-4" />
                  </a>
                </div>
              }
              templatesGalleryRoute={AppRoutes.STUDIO_TEMPLATES}
              templatesItemRoute={AppRoutes.STUDIO_TEMPLATES_ITEM}
              Link={Link}
            />
          </React.Suspense>
        </div>
        <NavigationMenuPrimitive.Root lang={lang} aria-label="Top navigation">
          <NavigationMenuPrimitive.List className="text-13 flex items-center gap-[6px] sm:gap-[10px]">
            <LanguageSwitcher
              ignoreLoggedInStyles
              classNames="text-[var(--navigation-text-color,_#000)] border border-transparent px-[10px] py-[6px] hover:border-gray-800 hover:bg-gray-900 hover:text-gray-500 focus-visible:border-gray-800 focus-visible:bg-gray-900 focus-visible:text-gray-500 [&>svg]:text-[var(--navigation-text-color,_#000)] [&>svg]:hover:text-gray-500  [&>svg]:focus-visible:text-gray-500"
            />
            <NavigationMenuPrimitive.Item>
              <StyledLink
                target="_blank"
                href={AppRoutes.STUDIO_TOOL}
                className="text-13 hidden min-h-0 items-center text-nowrap font-medium text-[var(--navigation-text-color,_#D1D5DB)] md:flex"
              >
                <File04Icon className="mr-2 h-auto w-5 shrink-0" />
                <span className="whitespace-nowrap">{t('buttons.my_projects')}</span>
              </StyledLink>
            </NavigationMenuPrimitive.Item>
            <div className="sm:hidden">
              <SearchMobile
                placeholderText={t('search.placeholder')}
                noResultsText={t('search.no_results')}
                allTemplatesLinkText={t('search.all_templates')}
              />
            </div>
            <NavigationMenuPrimitive.Item>
              <Button
                asChild
                variant="primary"
                className="text-13 text-service-white-white h-[42px] w-full justify-center text-nowrap bg-violet-700 font-medium sm:h-12 sm:min-w-[147px]"
                style={{ transform: 'translateZ(0)' }}
              >
                <Link
                  target="_blank"
                  href={AppRoutes.STUDIO_TOOL_CREATE}
                  className="text-13"
                  style={{ transform: 'translateZ(0)' }}
                >
                  {t('buttons.create_a_design')}
                </Link>
              </Button>
            </NavigationMenuPrimitive.Item>
          </NavigationMenuPrimitive.List>
        </NavigationMenuPrimitive.Root>
      </div>
      <div className="flex h-[47px] w-full items-center justify-between px-4">
        <div className="flex items-center gap-10">
          <div className="hidden xl:block">
            <NavigationMenu menuItems={menuItems} />
          </div>
        </div>
        <div className="flex items-center">
          <StyledLink
            target="_blank"
            href="https://www.creativefabrica.com/"
            className={`flex items-center text-nowrap font-medium ${iconColor}`}
            style={{ transform: 'translateZ(0)' }}
          >
            {t('buttons.browse_assets')}
            <ArrowNarrowRightIcon02
              className="stroke-3 ml-1 h-4 w-4"
              style={{ transform: 'translateZ(0)' }}
            />
          </StyledLink>
        </div>
      </div>
    </div>
  )
}
